// === WORKFLOW SUMMARY — sessionStorage chain state ===
// These keys are a cross-screen API: PaymentInformationScreen, AcceptanceQuestionsScreen,
// ConfirmationScreen, EndPageScreen, ProductSelectionScreen, PremiumBreakdownCard,
// utils/debugData and the confirmation email all read them by literal name.
// Never rename a key without migrating every consumer.

export const SUMMARY_SESSION_KEYS = {
  vpIdnr: 'vp_idnr',
  vpData: 'vp_data',
  calculationId: 'calculation_id',
  premiumCalculated: 'premium_calculated',
  packageDiscountApplied: 'package_discount_applied',
  quoteId: 'quote_id',
  quoteGenerated: 'quote_generated',
  enrichedDocument: 'enriched_calculation_document',
  chainCompletedHash: 'workflow_chain_completed_hash',
  relationDataHash: 'relation_data_hash',
} as const;

// Only ever removed, never written or read (writers were dropped in earlier passes, or the
// keys belong to other screens); kept in the full clear so a stale session leaves nothing behind.
const EXTRA_CLEARED_KEYS = [
  'workflow_session_active',
  'workflow_current_page',
  'workflow_completed',
] as const;

export const getVpIdnr = () => sessionStorage.getItem(SUMMARY_SESSION_KEYS.vpIdnr);
export const setVpIdnr = (value: string) => sessionStorage.setItem(SUMMARY_SESSION_KEYS.vpIdnr, value);

export const readVpData = (): any | null => {
  try {
    const stored = sessionStorage.getItem(SUMMARY_SESSION_KEYS.vpData);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};
export const writeVpData = (vpData: any) =>
  sessionStorage.setItem(SUMMARY_SESSION_KEYS.vpData, JSON.stringify(vpData));

export const getCalculationId = () => sessionStorage.getItem(SUMMARY_SESSION_KEYS.calculationId);
export const setCalculationId = (value: string) => sessionStorage.setItem(SUMMARY_SESSION_KEYS.calculationId, value);

export const getQuoteId = () => sessionStorage.getItem(SUMMARY_SESSION_KEYS.quoteId);
export const requestSubmittedKey = (vpIdnr: string, quoteId: string) => `request_submitted_${vpIdnr}_${quoteId}`;
export const setQuoteId = (value: string) => sessionStorage.setItem(SUMMARY_SESSION_KEYS.quoteId, value);

export const getChainCompletedHash = () => sessionStorage.getItem(SUMMARY_SESSION_KEYS.chainCompletedHash);
export const setChainCompletedHash = (hash: string) => sessionStorage.setItem(SUMMARY_SESSION_KEYS.chainCompletedHash, hash);

export const getRelationDataHash = () => sessionStorage.getItem(SUMMARY_SESSION_KEYS.relationDataHash);
export const setRelationDataHash = (hash: string) => sessionStorage.setItem(SUMMARY_SESSION_KEYS.relationDataHash, hash);

export type SummaryFlagKey =
  | typeof SUMMARY_SESSION_KEYS.premiumCalculated
  | typeof SUMMARY_SESSION_KEYS.packageDiscountApplied
  | typeof SUMMARY_SESSION_KEYS.quoteGenerated;

export const readFlag = (key: SummaryFlagKey) => sessionStorage.getItem(key) === 'true';

// True writes the literal 'true'; false REMOVES the key (readers elsewhere test presence).
export const writeFlag = (key: SummaryFlagKey, value: boolean) => {
  if (value) {
    sessionStorage.setItem(key, 'true');
  } else {
    sessionStorage.removeItem(key);
  }
};

/** Parsed enriched Relatiedocument, or null when absent or unparseable. */
export const readEnrichedDocument = (): any | null => {
  try {
    const stored = sessionStorage.getItem(SUMMARY_SESSION_KEYS.enrichedDocument);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

export const writeEnrichedDocument = (doc: any) =>
  sessionStorage.setItem(SUMMARY_SESSION_KEYS.enrichedDocument, JSON.stringify(doc));

/** Product data changed: drop everything derived from the old premiums, but keep vp_idnr so the same relation is reused. */
export const clearCalculationArtifacts = () => {
  sessionStorage.removeItem(SUMMARY_SESSION_KEYS.calculationId);
  sessionStorage.removeItem(SUMMARY_SESSION_KEYS.premiumCalculated);
  sessionStorage.removeItem(SUMMARY_SESSION_KEYS.quoteId);
  sessionStorage.removeItem(SUMMARY_SESSION_KEYS.quoteGenerated);
  sessionStorage.removeItem(SUMMARY_SESSION_KEYS.packageDiscountApplied);
  sessionStorage.removeItem(SUMMARY_SESSION_KEYS.enrichedDocument);
};

/**
 * Relation data (policyholder identity / VP address) changed: the stored relation itself is
 * stale, so keeping vp_idnr (as clearCalculationArtifacts does) would recalculate against the
 * OLD relation. Drop the relation ids plus everything calculated on top — the offer chain then
 * creates a fresh relation and re-runs calculate/package/quote against it.
 */
export const clearRelationAndCalculationArtifacts = () => {
  sessionStorage.removeItem(SUMMARY_SESSION_KEYS.vpIdnr);
  sessionStorage.removeItem(SUMMARY_SESSION_KEYS.vpData);
  sessionStorage.removeItem(SUMMARY_SESSION_KEYS.chainCompletedHash);
  clearCalculationArtifacts();
};

/** Safety clear when session IDs exist without workflow data (e.g. manual localStorage wipe). */
export const clearAllChainState = () => {
  Object.values(SUMMARY_SESSION_KEYS).forEach((key) => sessionStorage.removeItem(key));
  EXTRA_CLEARED_KEYS.forEach((key) => sessionStorage.removeItem(key));
};
